// @flow
import * as React from 'react';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { withRouter } from 'react-router-dom';
import { withLeaflet } from 'react-leaflet';
import { Box } from 'grommet';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import { FormattedMessage, injectIntl } from 'react-intl';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import GisPointIcon from 'esm-web-pro/components/layout/sidePanel/icons/GisPointIcon';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import GisPointAttributes from 'esm-web-pro/containers/pages/network/objects/gispoint/GisPointAttributes';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import GisPointActions from './GisPointActions';

type Props = {
    panelId: String,
    object: Object,
    map: Object,
    updateObject: Function,
    editor: BaseEditor,
    history: Object,
    match: Object,
    resource: Object,
    objectLayer: Object,
    leaflet: LeafletContext,
}

const panToPoint = (map, point) => {
    const pointCoords = [point.coordinates.coordinates[1], point.coordinates.coordinates[0]];
    if (map) {
        map.highlightedGeometry = {
            bounds: null, center: pointCoords,
        };
        map.panTo(pointCoords);
    }
};

const GisPoint = (props: Props) => {
    const {
        object: point, resource, map: networkMap, editor, updateObject, history, match, objectLayer, leaflet: { map },
        panelId,
    } = props;

    const focusPoint = UseHighlightPoint(null, point.sequence, point.identity);
    const memoizedFocusPoint = React.useCallback(focusPoint,
        [point.identity, point.sequence]);

    const handleEdit = (data: Object) => {
        updateObject(data);
    };

    const handleView = () => {
        panToPoint(map, point);
        editor.unregisterAllLoadingCallback();
        editor.registerLoadingCallback(focusPoint);
    };

    React.useEffect(() => {
        panToPoint(map, point);
        editor.registerLoadingCallback(memoizedFocusPoint);
        return () => {
            editor.unregisterLoadingCallback(memoizedFocusPoint);
        };
    }, [editor, memoizedFocusPoint, map, point]);

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={point.sequence}
            titleLabel={<FormattedMessage id="gispoint-panel-title" defaultMessage="Point" />}
            titleIcon={<GisPointIcon />}
            titleOnView={handleView}
            actions={<NavigateBackButton panelId={panelId} />}
        >
            <Box height="100%" justify="between">
                <Box flex>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <GisPointAttributes point={point} url={match.url} />
                        <HorizontalLine color="grey" />
                        <Box direction="row">
                            <GisPointActions
                                point={point}
                                objectLayer={objectLayer}
                                map={networkMap}
                                onEdit={handleEdit}
                                resource={resource}
                                editor={editor}
                                history={history}
                                match={match}
                                updateObject={updateObject}
                            />
                        </Box>
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                    <BottomContainer />
                </Box>
            </Box>
        </SidePane>
    );
};

export default withRouter(withLeaflet(injectIntl(GisPoint)));
